import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';

interface Row {
  id: string;
  ts: number;
  actor_email: string;
  actor_role: string;
  target_workspace?: string | null;
  action: string;
  reason?: string | null;
  thread_id?: string | null;
}

export function AdminAudit() {
  const [actor, setActor] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [action, setAction] = useState('');
  const { data, isPending } = useQuery({
    queryKey: ['admin', 'audit', actor, workspace, action],
    queryFn: () => {
      const params = new URLSearchParams();
      if (actor) params.set('actor_email', actor);
      if (workspace) params.set('target_workspace', workspace);
      if (action) params.set('action', action);
      return api.get<{ entries: Row[] }>(`/api/audit?${params.toString()}`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="text-base font-semibold">Filters</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            className="input"
            placeholder="Actor email"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          />
          <input
            className="input"
            placeholder="Workspace id"
            value={workspace}
            onChange={(e) => setWorkspace(e.target.value)}
          />
          <input
            className="input"
            placeholder="Action prefix"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        </div>
      </div>
      <div className="card">
        <h3 className="text-base font-semibold">Audit log</h3>
        {isPending ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <table className="mt-3 min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Time</th>
                <th className="text-left py-2">Actor</th>
                <th className="text-left py-2">Action</th>
                <th className="text-left py-2">Workspace</th>
                <th className="text-left py-2">Thread</th>
              </tr>
            </thead>
            <tbody>
              {(data?.entries ?? []).map((r) => (
                <tr key={r.id} className="border-b last:border-b-0">
                  <td className="py-2">{new Date(r.ts).toLocaleString()}</td>
                  <td className="py-2 font-mono text-xs">{r.actor_email}</td>
                  <td className="py-2">{r.action}</td>
                  <td className="py-2 font-mono text-xs">{r.target_workspace ?? '—'}</td>
                  <td className="py-2 font-mono text-xs">
                    {r.thread_id ? (
                      <button
                        className="text-brand-700 hover:underline"
                        onClick={() => navigator.clipboard.writeText(r.thread_id!)}
                        title="Copy thread id"
                      >
                        {r.thread_id}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
              {(!data?.entries || data.entries.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-slate-400">
                    No audit entries.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
